// Mirrors middleware's `src/tournament/interfaces/limited-link.interface.ts`. When a shape
// changes there, change it here in the same story.

export interface AgentInfo {
  agentId: string;
  // Admins see every agent's figures and may issue open links; agents see only their own.
  admin: boolean;
  trackingEnabled: boolean;
}

export interface IssueTournament {
  id: string;
  title: string;
  endAt: string;
}

export interface IssueOptions {
  tournaments: IssueTournament[];
  agent: { agentId: string; admin: boolean };
  locales: string[];
  defaultLocale: string;
}

export interface IssueLinkBody {
  tournamentId: string;
  // Left out, the link is open: any logged-in player can join with it. Admins only.
  playerUuid?: string;
  locale: string;
}

export interface IssuedLink {
  url: string;
  expiresAt: string;
}

export interface CohortSummary {
  linksIssued: number;
  playersInvited: number;
  playersRedeemed: number;
  redeemRate: number | null;
}

export type EventName = 'issued' | 'redeemed';

export interface EventItem {
  occurredAt: string;
  event: EventName;
  tournamentId: string;
  // For a join through an open link, the agent it is credited to.
  agentId: string;
  // null on the issue of an open link.
  playerUuid: string | null;
  linkId: string;
  alreadyJoined: boolean | null;
}

export interface OverviewTournament extends CohortSummary {
  tournamentId: string;
  agentIds: string[];
  firstIssuedAt: string;
  lastEventAt: string;
  title: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
}

export interface OverviewAgent extends CohortSummary {
  agentId: string;
  lastEventAt: string;
}

export interface Overview {
  agent: { agentId: string; admin: boolean };
  totals: CohortSummary;
  tournaments: OverviewTournament[];
  agents: OverviewAgent[];
  recent: EventItem[];
  eventsCount: number;
}

export type WindowName = 'before' | 'during' | 'after7' | 'after30';

export interface WindowMetrics {
  activePlayers: number;
  retentionRate: number | null;
  depositors: number;
  depositCount: number;
  depositEur: number;
  ggrEur: number;
  ngrEur: number;
}

export interface AgentResults extends CohortSummary {
  agentId: string | null;
  retention: Record<WindowName, WindowMetrics>;
}

export interface WindowsInfo {
  before: { days: number; partial: false };
  during: { to: string; partial: boolean };
  after7: { from: string; to: string; partial: boolean; daysAvailable: number };
  after30: { from: string; to: string; partial: boolean; daysAvailable: number };
}

export interface TournamentResults {
  ok: true;
  tournament: { id: string; startAt: string; endAt: string };
  windows: WindowsInfo;
  totals: AgentResults;
  agents: AgentResults[];
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; eventsCount: number };
}

export interface PlayerProfile {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  country: string | null;
  currency: string | null;
  registrationDate: string | null;
  status: string | null;
}

export interface PlayerWindowMetrics {
  active: boolean;
  deposited: boolean;
  depositCount: number;
  depositEur: number;
  ggrEur: number;
  ngrEur: number;
}

export interface PlayerRow {
  playerUuid: string;
  agentIds: string[];
  linksIssued: number;
  locale: string | null;
  // null when nobody invited the player: they joined through an open link.
  invitedAt: string | null;
  redeemedAt: string | null;
  joinedViaOpenLink: boolean;
  profile: PlayerProfile | null;
  // ext_profile's `is_test`. True is why `profile` is null: test accounts are left out
  // of the player data bigquery-service mirrors, while their activity is still counted.
  isTest: boolean;
  lastActiveDay: string | null;
  windows: Record<WindowName, PlayerWindowMetrics> | null;
}

export interface TournamentPlayers {
  ok: true;
  tournament: { id: string; startAt: string; endAt: string };
  windows: WindowsInfo;
  players: PlayerRow[];
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; eventsCount: number };
}

// ---- daily wheel spin history (HIS-484) — mirrors middleware's DailyWheelAdminSpin ----------

export type WheelTier = 'bronze' | 'silver' | 'gold';
export const WHEEL_TIERS: readonly WheelTier[] = ['bronze', 'silver', 'gold'];

// `drawn` is a spin whose grant never settled (middleware crashed between draw and credit);
// `declined` is a win the player passed on to keep their current bonus.
export type WheelSpinStatus = 'drawn' | 'pending_choice' | 'granted' | 'failed' | 'declined';
export const WHEEL_SPIN_STATUSES: readonly WheelSpinStatus[] = [
  'granted',
  'failed',
  'pending_choice',
  'declined',
  'drawn',
];

export interface WheelSpin {
  id: number;
  playerUuid: string;
  // UTC calendar day the spin counted for.
  day: string;
  // Tier the deposit entitled the player to.
  tier: WheelTier;
  // Wheel actually spun (a missing tier wheel falls down a tier).
  wheelUid: string;
  wheelTier: WheelTier;
  segmentUid: string;
  segmentLabel: string | null;
  campaignUuid: string | null;
  status: WheelSpinStatus;
  replacedCampaignUuid: string | null;
  depositTotal: number | null;
  depositCurrency: string | null;
  spunAt: string;
  settledAt: string | null;
}

export interface WheelSpinsPage {
  items: WheelSpin[];
  nextCursor: string | null;
}

// Empty string / empty list = no filter.
export interface WheelSpinsFilters {
  // As the ledger stores it (PLAYER-<uuid>), matched exactly.
  playerUuid: string;
  tier: WheelTier | '';
  // Any of these.
  statuses: WheelSpinStatus[];
  wheelUid: string;
  segmentUid: string;
  // Of the deposit that unlocked the spin.
  currency: string;
  // Inclusive UTC days.
  from: string;
  to: string;
}

// Mirrors middleware's DailyWheelAdminSpinsSummary: the spins the filters match, counted; every
// list largest first. A prize is one slice of one wheel.
export interface WheelSpinsSummary {
  spins: number;
  players: number;
  byStatus: { status: WheelSpinStatus; spins: number }[];
  byTier: { tier: WheelTier; spins: number }[];
  byPrize: { wheelUid: string; segmentUid: string; segmentLabel: string | null; spins: number }[];
  byCurrency: { currency: string | null; spins: number }[];
}

// ---- whales (HIS-507) — mirrors middleware's whales.interface.ts --------------------------

// The Tier 1 players by their first 72 hours from the FTD. from / to filter the FTD day.
// Which whales the page lists: `vip` = all-time deposits above vipRule.depositEurAbove (EUR 1,000),
// however many; `potential-vip` = the Tier 1 players (tier1Rule) who are not VIP yet.
export type WhalesSegment = 'potential-vip' | 'vip';

export interface WhalesFilters {
  segment: WhalesSegment;
  // UTC days; empty is no bound.
  from: string;
  to: string;
  limit: number;
}

export interface WhaleProfile {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  currency: string | null;
  status: string | null;
}

export interface WhaleRow {
  playerUuid: string;
  // null only means the players mirror has not caught up: test accounts never reach this list.
  profile: WhaleProfile | null;
  country: string | null;
  // All time, EUR. NGR is the analysts' mart figure.
  depositCount: number;
  depositEur: number;
  withdrawalEur: number;
  betEur: number;
  ggrEur: number;
  ngrEur: number;
  rewardEur: number;
  cashbackEur: number;
  bonusConvertedEur: number;
  lastActiveDay: string | null;
  // The game type with the most real-money bets, all time, and its share of them.
  mainGame: { gameType: string; betShare: number } | null;
  // The latest CRM message, segment inclusion or reward over activity.comms.
  lastComm: { channel: string; name: string | null; at: string } | null;
  // CRM messages per contact channel over activity.comms.
  channels: WhaleChannel[];
  // Support agents' notes on the player, newest first.
  notes: PlayerNote[];
}

// A support agent's free-text note on a player (mirrors middleware's player-note.interface.ts).
export interface PlayerNote {
  id: number;
  // Bare lowercase player uuid.
  playerUuid: string;
  agentId: string;
  body: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface WhaleChannel {
  channel: string;
  sent: number;
  // Followed by a completed real-money deposit within 24 hours.
  deposited24h: number;
}

// Tier 1: in the `hours` from the FTD (FTD included), minDeposits+ deposits totalling
// minDepositEur+ (latamMinDepositEur+ for latamCountries).
export interface WhalesTier1Rule {
  hours: number;
  minDeposits: number;
  minDepositEur: number;
  latamMinDepositEur: number;
  latamCountries: string[];
}

// VIP: all-time deposits strictly above depositEurAbove; potential VIP: at or below it.
export interface WhalesVipRule {
  depositEurAbove: number;
}

export interface DayRange {
  from: string;
  to: string;
}

export interface WhalesReport {
  ok: true;
  segment: WhalesSegment;
  // The FTD-day filter as applied; null = no bound.
  from: string | null;
  to: string | null;
  limit: number;
  // UTC days the mart mirror holds (all time); null until the whales data is synced.
  coverage: DayRange | null;
  tier1Rule: WhalesTier1Rule;
  vipRule: WhalesVipRule;
  // UTC days the game and CRM mirrors hold, and the first FTD day to the day the Tier 1
  // table was rebuilt; null until their first sync.
  activity: { games: DayRange | null; comms: DayRange | null; ftd: DayRange | null };
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; source?: string; lastSyncAt?: string | null };
  rows: WhaleRow[];
}

// Traffic quality: the affiliate / country / outlier report built from the analysts' tq tables. The
// report's own script reads the rows, so only `meta` is typed field by field here.
export interface TrafficQualityMeta {
  data_through: string;
  generated: string;
  source: string;
  grain: string;
  ret_threshold: number;
  fence: { dep_fence_eur: number; players: number; method: string; outliers: number };
}

export interface TrafficQualityData {
  meta: TrafficQualityMeta;
  groups: Record<string, unknown>[];
  regs: Record<string, unknown>[];
  outliers: Record<string, unknown>[];
}

// Mirrors middleware's traffic-quality.interface.ts (which mirrors bigquery-service's
// TrafficQualityResponse). `report` is null until bigquery-service's first sync.
export interface TrafficQualityResponse {
  ok: true;
  dataThrough: string | null;
  generatedAt: string | null;
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; source?: string; lastSyncAt?: string | null };
  report: TrafficQualityData | null;
}

// ---- Console reports: the analysts' other reports, built nightly in BigQuery ----

export type ConsoleReportId = 'ar-traffic' | 'deposit-timing' | 'aff-trend' | 'aff-comparison';

// A report's data as the analysts' build holds it: the standalone page's `D` object plus the
// period it covers. The page's own script reads it, so only `meta` is typed here.
export interface ConsoleReportData {
  meta: { data_through: string; generated: string; [key: string]: unknown };
  [key: string]: unknown;
}

// Mirrors middleware's console-report.interface.ts (which mirrors bigquery-service's
// ConsoleReportResponse). `data` is null until bigquery-service's first sync.
export interface ConsoleReportResponse {
  ok: true;
  report: string;
  dataThrough: string | null;
  generatedAt: string | null;
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; source?: string; lastSyncAt?: string | null };
  data: ConsoleReportData | null;
}
